/**
 * @file ClientSettingDataPoints.js
 * @description Component to view and edit the Data Points Enabled client option.
 */

import { useEffect, useState } from 'react';
import {
    createClientOption,
    getClientOptions,
    updateClientOption,
} from '../../services/clientService';
import Toast from '../ui/toasts/Toast';
import { ClientOptionEnum, SecurityAttributes } from '../../utils/data/enums';
import { useAuthContext } from '../../store/Contexts';
import { getText } from '../../i18n';

function ClientSettingDataPoints() {
    const [isOpen, setIsOpen] = useState(true);
    const [initialDataPointsSetting, setInitialDataPointsSetting] = useState(
        {},
    );
    const [selectedDataPointsEnabled, setSelectedDataPointsEnabled] =
        useState('false');
    const { hasAttribute } = useAuthContext();

    useEffect(() => {
        async function loadDataPointsOption() {
            try {
                const data = await getClientOptions(0, [
                    ClientOptionEnum.DataPointsEnabled,
                ]);
                const optionObject = data.find(
                    item =>
                        item.clientOptionId ===
                        ClientOptionEnum.DataPointsEnabled,
                );

                if (!optionObject) {
                    setInitialDataPointsSetting({
                        clientOptionId: ClientOptionEnum.DataPointsEnabled,
                        usingDefaultValue: true,
                        value: 'false',
                    });
                    setSelectedDataPointsEnabled('false');
                    return;
                }

                setInitialDataPointsSetting(optionObject);
                setSelectedDataPointsEnabled(optionObject.value);
            } catch (error) {
                console.error('Could not fetch Data Points setting:', error);
                Toast.error(getText('msg_e46063867f99'));
            }
        }

        loadDataPointsOption();
    }, []);

    function toggleAccordion() {
        setIsOpen(!isOpen);
    }

    function handleToggleDataPointsEnabled() {
        setSelectedDataPointsEnabled(prev =>
            prev === 'true' ? 'false' : 'true',
        );
    }

    async function handleSave() {
        let noChanges = true;

        if (initialDataPointsSetting.usingDefaultValue) {
            noChanges = false;
            const option = {
                clientOptionId: ClientOptionEnum.DataPointsEnabled,
                value: selectedDataPointsEnabled,
            };
            const response = await createClientOption(option, 0);

            if (response.error) {
                Toast.error(getText('msg_d4d8d06a3946'));
                return;
            }

            setInitialDataPointsSetting(response.data);
        }

        if (
            !initialDataPointsSetting.usingDefaultValue &&
            initialDataPointsSetting.value !== selectedDataPointsEnabled
        ) {
            noChanges = false;
            const option = {
                clientOptionId: ClientOptionEnum.DataPointsEnabled,
                value: selectedDataPointsEnabled,
            };
            const response = await updateClientOption(option, 0);

            if (response.error) {
                Toast.error(getText('msg_b52f1836897d'));
                return;
            }

            setInitialDataPointsSetting(response.data);
        }

        if (noChanges) {
            Toast.warning(getText('msg_13c3a7ac4e47'));
            return;
        }

        Toast.success(getText('msg_a3d10ada7088'));
    }

    function handleCancel() {
        setSelectedDataPointsEnabled(initialDataPointsSetting.value);
    }

    return (
        <div
            style={{
                overflow: 'hidden',
                borderRadius: '12px',
                border: 'solid 1px rgba(47, 43, 67, 0.2)',
                marginTop: '10px',
                boxShadow:
                    'rgba(47, 43, 67, 0.1) 0px -1px 0px 0px inset, rgba(47, 43, 67, 0.1) 0px 1px 3px 0px',
            }}
        >
            <div
                style={{
                    overflowY: 'auto',
                    padding: '16px',
                    resize: 'none',
                    boxShadow:
                        'rgba(47, 43, 67, 0.1) 0px -1px 0px 0px inset, rgba(47, 43, 67, 0.1) 0px 1px 3px 0px;',
                }}
            >
                <div>
                    <div style={{ marginLeft: '12px' }}>
                        {getText('msg_1877c58443d0')}
                        <i
                            style={{
                                color: 'var(--iNGAGED-Neutral-Color3)',
                                display: 'flex',
                                float: 'inline-end',
                                cursor: 'pointer',
                            }}
                            className={`bi bi-chevron-${isOpen ? 'up' : 'down'}`}
                            onClick={toggleAccordion}
                        />
                    </div>
                </div>
                {isOpen && (
                    <div
                        style={{
                            marginLeft: '48px',
                            marginRight: '48px',
                            marginTop: '12px',
                            color: 'var(--iNGAGED-Neutral-Color2)',
                        }}
                    >
                        <div style={{ marginTop: '12px' }}>
                            <p>{getText('msg_559be43cf8cc')}</p>
                        </div>

                        <div style={{ marginLeft: '24px' }}>
                            <div
                                style={{
                                    display: 'flex',
                                    gap: '5px',
                                    alignItems: 'anchor-center',
                                    marginTop: '12px',
                                }}
                            >
                                <div className="form-switch">
                                    <input
                                        onClick={handleToggleDataPointsEnabled}
                                        className="form-check-input custom-switch-input"
                                        type="checkbox"
                                        checked={
                                            selectedDataPointsEnabled === 'true'
                                        }
                                    />
                                </div>
                                <div>{getText('msg_32a9af10d26f')}</div>
                            </div>
                        </div>
                        {hasAttribute(
                            SecurityAttributes.ClientSettingsFullAccess,
                        ) && (
                            <div className="d-flex justify-content-evenly mt-5">
                                <button
                                    type="button"
                                    onClick={handleCancel}
                                    className="ing-popup-blue-negative"
                                >
                                    {getText('msg_77dfd2135f4d')}
                                </button>
                                <button
                                    type="button"
                                    className="ing-button-primary-none"
                                    onClick={handleSave}
                                >
                                    {getText('msg_efc007a393f6')}
                                </button>
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
}

export default ClientSettingDataPoints;
